import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEffectiveClaudeDesignExportEnabled } from '@blueprint/shared';
import { isPhase25Complete } from '../lib/constants';

// DISC.B1 (#1476) — "Ready for Claude Design" dashboard banner.
//
// Fires at the exact moment Phase 2.5 completes — surfacing the Claude Design
// round-trip workflow without requiring prior knowledge of it. ALL of:
//   • all 5 Phase 2.5 artefacts approved   (isPhase25Complete)
//   • the EFFECTIVE Claude Design export toggle (F4) is on — per-project
//     override wins, else the tenant default, fail-closed (shared resolver,
//     the same one the F2 export gate uses). NOT a bare tenant-flag read.
//   • the user hasn't dismissed it (localStorage, per-project key)
//
// Client-side derivation only — no new API endpoint. Renders independently of
// latestSession; mounted in the dashboard's left column below the phase-gate
// status row.

interface BannerArtefact {
  id: string;
  type: string;
  status: string;
}

interface Props {
  projectId: string;
  artefacts: ReadonlyArray<BannerArtefact>;
  /** Per-project Claude Design export override (F4); null/undefined = inherit tenant default. */
  claudeDesignExportEnabled: boolean | null | undefined;
  /** Tenant-level Claude Design export default (F4); from useMyTenant. */
  tenantClaudeDesignExportEnabled: boolean | null | undefined;
}

function dismissKey(projectId: string): string {
  return `blueprint_cd_ready_dismissed_${projectId}`;
}

// localStorage can throw (private mode / disabled storage / quota). Treat any
// failure as "not dismissed" so the banner degrades to visible, never crashes.
function readDismissed(projectId: string): boolean {
  try {
    return localStorage.getItem(dismissKey(projectId)) === '1';
  } catch {
    return false;
  }
}

export default function ClaudeDesignReadyBanner({
  projectId,
  artefacts,
  claudeDesignExportEnabled,
  tenantClaudeDesignExportEnabled,
}: Props) {
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(projectId));

  // Re-read on project switch (the banner instance can be reconciled across
  // route changes rather than remounted).
  useEffect(() => {
    setDismissed(readDismissed(projectId));
  }, [projectId]);

  const exportEnabled = getEffectiveClaudeDesignExportEnabled(
    claudeDesignExportEnabled,
    tenantClaudeDesignExportEnabled,
  );
  const phase25Complete = isPhase25Complete(artefacts);

  if (!exportEnabled || !phase25Complete || dismissed) return null;

  // Guaranteed present whenever phase25Complete (screen_spec is one of the
  // five required-approved types), but guard the CTA defensively.
  const screenSpecId = artefacts.find(a => a.type === 'screen_spec')?.id;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(projectId), '1');
    } catch {
      /* storage unavailable — banner stays dismissed for this session via state */
    }
    setDismissed(true);
  };

  return (
    <div
      data-testid="claude-design-ready-banner"
      style={{
        position: 'relative',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
        borderLeftColor: 'var(--primary)',
        borderRadius: 'var(--radius-sm)',
        padding: '14px 40px 14px 16px',
      }}
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        data-testid="claude-design-ready-dismiss"
        style={{
          position: 'absolute', top: 10, right: 12,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 16, lineHeight: 1, color: 'var(--text-secondary)', padding: 0,
        }}
      >
        ✕
      </button>

      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>
        Your Phase 2.5 design system is complete
      </h3>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
        You can now refine your screen specs in Claude Design before opening Sprint Planning.
        Export your specs from the Screen Specifications artefact, design in Claude Design, then
        import the handoff bundle here.
      </p>

      {screenSpecId && (
        <Link
          to={`/artefacts/${screenSpecId}`}
          data-testid="claude-design-ready-cta"
          style={{
            display: 'inline-block',
            fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
            padding: '6px 14px', color: 'white', background: 'var(--primary)',
            borderRadius: 'var(--radius-sm)', textDecoration: 'none',
          }}
        >
          View Screen Specifications
        </Link>
      )}
    </div>
  );
}
